import type { VisualizationTypesCatalogBody } from '../../models/catalog.types'
import {
  RENDERABLE_CHART_TYPES,
  type ChartAvailabilityInput,
  type ChartType,
  type ChartTypeMeta,
  type VisualizationTypeDefinition,
} from '../../models/chart.types'

import { chartRendererRegistry } from './chartRendererRegistry'
import {
  applyFieldRoleSelectionRules,
  getFieldRoleFields,
  normalizeChartFieldRoles,
  toChartFieldCandidates,
} from './fieldRoles'

export { chartRendererRegistry } from './chartRendererRegistry'

/** Used only while the backend catalog is unavailable. It mirrors the local renderer contract,
 *  never types that merely happen to exist in Highcharts. */
export const fallbackChartTypeCatalog: readonly ChartTypeMeta[] = chartRendererRegistry.map(
  (renderer) => ({
    type: renderer.type,
    nameKey: renderer.nameKey,
    label: null,
    description: null,
    minDimensions: renderer.minDimensions,
    maxDimensions: renderer.maxDimensions,
    minMetrics: renderer.minMetrics,
    maxMetrics: renderer.maxMetrics,
    requiresTimeDimension: renderer.requiresTimeDimension,
    defaultLimit: renderer.defaultLimit,
    maxLimit: renderer.defaultLimit,
    maxTimeDimensions: 1,
    minDataPoints: 1,
    requiresComparison: renderer.requiresComparison,
    requiresHierarchy: false,
    requiresPairs: false,
    isWidget: false,
    maxSeries: null,
    maxSmallMultiples: null,
    defaultOptions: null,
    fieldRoles: [],
    fieldRolesValid: true,
  }),
)

function nonNegativeInteger(value: unknown, fallback: number): number {
  return typeof value === 'number' && Number.isInteger(value) && value >= 0 ? value : fallback
}

function nullableNonNegativeInteger(value: unknown): number | null {
  return typeof value === 'number' && Number.isInteger(value) && value >= 0 ? value : null
}

function text(value: unknown): string | null {
  return typeof value === 'string' && value.trim() ? value.trim() : null
}

function plainObject(value: unknown): Readonly<Record<string, unknown>> | null {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null
}

/** Defensive wire normalizer. Unknown/custom keys are retained as strings for diagnostics and
 *  future renderers, but malformed and inactive entries never reach capability selection. Which
 *  keys the row carried is kept too, so resolution can tell "not sent" from a sent default. */
export function normalizeVisualizationTypes(
  body: VisualizationTypesCatalogBody,
): VisualizationTypeDefinition[] {
  if (!Array.isArray(body.results)) return []

  const seen = new Set<string>()
  const normalized: VisualizationTypeDefinition[] = []
  for (const row of body.results) {
    if (row.is_active === false) continue
    const kind = row.visualization_kind
    if (kind !== 'chart' && kind !== 'table') continue
    const type = text(
      row.visualization_type ?? (kind === 'chart' ? row.chart_type : row.table_type),
    )
    if (!type) continue
    const identity = `${kind}:${type}`
    if (seen.has(identity)) continue
    seen.add(identity)

    const normalizedRoles = normalizeChartFieldRoles(row.field_roles)

    normalized.push({
      kind,
      type,
      label: text(row.label),
      description: text(row.description),
      minDimensions: nonNegativeInteger(row.min_dimensions, 0),
      maxDimensions: nullableNonNegativeInteger(row.max_dimensions),
      minMetrics: nonNegativeInteger(row.min_metrics, kind === 'chart' ? 1 : 0),
      maxMetrics: nullableNonNegativeInteger(row.max_metrics),
      requiresTimeDimension: row.requires_time_dimension === true,
      defaultLimit: nullableNonNegativeInteger(row.default_limit),
      maxLimit: nullableNonNegativeInteger(row.max_limit),
      maxTimeDimensions: nonNegativeInteger(row.max_time_dimensions, 1),
      minDataPoints: nonNegativeInteger(row.min_data_points, 1),
      requiresComparison: row.requires_comparison === true,
      requiresHierarchy: row.requires_hierarchy === true,
      requiresPairs: row.requires_pairs === true,
      isWidget: row.is_widget === true,
      maxSeries: nullableNonNegativeInteger(row.max_series),
      maxSmallMultiples: nullableNonNegativeInteger(row.max_small_multiples),
      defaultOptions: plainObject(row.default_options),
      fieldRoles: normalizedRoles.roles,
      fieldRolesValid: normalizedRoles.valid,
      suppliedFields: new Set(Object.keys(row).filter((key) => row[key] !== undefined)),
    })
  }
  return normalized
}

function isRenderableChartType(value: string): value is ChartType {
  return (RENDERABLE_CHART_TYPES as readonly string[]).includes(value)
}

function clampLimit(value: number | null, maximum: number | null): number | null {
  if (value === null || maximum === null) return value
  return Math.min(value, maximum)
}

type Renderer = (typeof chartRendererRegistry)[number]

/** The backend's roles drive the picker whenever they carry every slot the builder needs; extra
 *  optional slots are harmless. A row that sends no roles falls back to the builder's positional
 *  field order, like the degraded catalog. Roles that were sent but are malformed, or that miss a
 *  slot the builder requires, cannot be rendered faithfully, so the type is withheld (`null`). */
function resolvedFieldRoles(
  renderer: Renderer,
  definition: VisualizationTypeDefinition,
): VisualizationTypeDefinition['fieldRoles'] | null {
  if (!definition.suppliedFields.has('field_roles')) return []
  if (!definition.fieldRolesValid) return null
  const backendRoles = new Map(definition.fieldRoles.map((role) => [role.key, role]))
  const compatible = renderer.roles.every((expected) => {
    const actual = backendRoles.get(expected.key)
    return actual ? actual.fieldType === expected.fieldType : !expected.required
  })
  return compatible ? definition.fieldRoles : null
}

/** Resolve one backend row onto its builder. Every constraint the backend sent wins, including an
 *  explicit `null`; the local registry answers only for keys the row left out. */
function resolveChartTypeMeta(
  renderer: Renderer,
  definition: VisualizationTypeDefinition,
): ChartTypeMeta | null {
  const sent = (key: string): boolean => definition.suppliedFields.has(key)
  const minDimensions = sent('min_dimensions') ? definition.minDimensions : renderer.minDimensions
  const maxDimensions = definition.maxDimensions ?? renderer.maxDimensions
  const minMetrics = sent('min_metrics') ? definition.minMetrics : renderer.minMetrics
  const maxMetrics = definition.maxMetrics ?? renderer.maxMetrics
  if (minDimensions > maxDimensions || minMetrics > maxMetrics) return null

  const maxLimit = sent('max_limit') ? definition.maxLimit : renderer.defaultLimit
  const defaultLimit = sent('default_limit') ? definition.defaultLimit : renderer.defaultLimit
  const fieldRoles = resolvedFieldRoles(renderer, definition)
  if (!fieldRoles) return null

  return {
    type: renderer.type,
    nameKey: renderer.nameKey,
    label: definition.label,
    description: definition.description,
    minDimensions,
    maxDimensions,
    minMetrics,
    maxMetrics,
    requiresTimeDimension: sent('requires_time_dimension')
      ? definition.requiresTimeDimension
      : renderer.requiresTimeDimension,
    defaultLimit: clampLimit(defaultLimit, maxLimit),
    maxLimit,
    maxTimeDimensions: definition.maxTimeDimensions,
    minDataPoints: definition.minDataPoints,
    requiresComparison: sent('requires_comparison')
      ? definition.requiresComparison
      : renderer.requiresComparison,
    requiresHierarchy: definition.requiresHierarchy,
    requiresPairs: definition.requiresPairs,
    isWidget: false,
    maxSeries: definition.maxSeries,
    maxSmallMultiples: definition.maxSmallMultiples,
    defaultOptions: definition.defaultOptions,
    fieldRoles: fieldRoles.map((role) =>
      role.fieldType === 'metric' && role.multiple
        ? { ...role, max: Math.min(role.max, maxMetrics) }
        : role,
    ),
    fieldRolesValid: true,
  }
}

/** Backend-first chart catalog. The backend decides which types exist and every constraint it
 *  sends; a type is offered when module 08 has a builder for it. The local registry only fills
 *  constraints a backend row omits (and replaces the whole catalog while it is unavailable). */
export function resolveChartTypeCatalog(
  definitions: readonly VisualizationTypeDefinition[],
): ChartTypeMeta[] {
  const seen = new Set<string>()
  return definitions.flatMap((definition) => {
    if (definition.kind !== 'chart' || definition.isWidget) return []
    if (!isRenderableChartType(definition.type) || seen.has(definition.type)) return []
    seen.add(definition.type)
    const renderer = chartRendererRegistry.find((candidate) => candidate.type === definition.type)
    const meta = renderer ? resolveChartTypeMeta(renderer, definition) : null
    return meta ? [meta] : []
  })
}

/** Enforce both ends of the effective backend/renderer range before the field-role selector is
 *  mounted, so a type can never be offered with an impossible assignment. */
export function getAvailableChartTypes(
  catalog: readonly ChartTypeMeta[],
  input: ChartAvailabilityInput,
): ChartTypeMeta[] {
  const allowed = new Set(input.allowedTypes ?? RENDERABLE_CHART_TYPES)

  return catalog.filter(
    (meta) =>
      allowed.has(meta.type) &&
      hasCompatibleRequestShape(meta, input) &&
      (!meta.requiresComparison || input.hasComparison === true) &&
      (!meta.requiresHierarchy || (input.hasHierarchy ?? input.dimensions.length > 0)) &&
      (!meta.requiresPairs || (input.hasPairs ?? input.dimensions.length > 1)) &&
      (input.dataPointCount === undefined ||
        isChartDataPointCountCompatible(meta, input.dataPointCount)),
  )
}

/** Point counts are response-specific. Callers may use this only for the candidate whose exact
 * request has settled; applying one candidate's count to the entire picker leaks query shape. */
export function isChartDataPointCountCompatible(
  meta: Pick<ChartTypeMeta, 'minDataPoints' | 'maxLimit'>,
  count: number,
): boolean {
  return count >= meta.minDataPoints && (meta.maxLimit === null || count <= meta.maxLimit)
}

function hasCompatibleRequestShape(meta: ChartTypeMeta, input: ChartAvailabilityInput): boolean {
  const timeCandidates = new Set(input.timeDimensions ?? [])
  if (meta.fieldRoles.length === 0) {
    return (
      input.dimensions.length >= meta.minDimensions &&
      input.dimensions.length <= meta.maxDimensions &&
      input.metrics.length >= meta.minMetrics &&
      input.metrics.length <= meta.maxMetrics &&
      timeCandidates.size <= meta.maxTimeDimensions &&
      (!meta.requiresTimeDimension || input.dimensions.some((field) => timeCandidates.has(field)))
    )
  }

  const resolution = applyFieldRoleSelectionRules(meta.fieldRoles, {
    dimensions: toChartFieldCandidates(input.dimensions),
    metrics: toChartFieldCandidates(input.metrics),
    timeDimensions: toChartFieldCandidates(input.timeDimensions ?? []),
  })
  if (!resolution.valid) return false

  const fields = getFieldRoleFields(meta.fieldRoles, resolution.selection)
  const dimensionCount = new Set([...fields.dimensions, ...fields.timeDimensions]).size
  return (
    dimensionCount >= meta.minDimensions &&
    dimensionCount <= meta.maxDimensions &&
    fields.metrics.length >= meta.minMetrics &&
    fields.metrics.length <= meta.maxMetrics &&
    fields.timeDimensions.length <= meta.maxTimeDimensions &&
    (!meta.requiresTimeDimension || fields.timeDimensions.length > 0)
  )
}
